import {
  IconChevronDown,
  IconChevronUp,
  IconPencil,
} from "@tabler/icons-react";
import { createPortal } from "react-dom";
import { useLayoutEffect, useState } from "react";
import "@assets/css/maps/RouteActionsOverlayPanel.css";

function AiToolsPanel({
  anchorRef,
  title = "AI TOOLS",
  actions = [],
  ariaLabel = "AI Tools",
  isVisible = true,
  isToggleDisabled = false,
}) {
  const [position, setPosition] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  useLayoutEffect(() => {
    if (!isVisible || !anchorRef?.current) return undefined;
    const anchor = anchorRef.current;

    const updatePosition = () => {
      const bounds = anchor.getBoundingClientRect();
      const panelWidth = Math.min(256, window.innerWidth - 24);
      let left = bounds.right + 12;
      if (left + panelWidth > window.innerWidth - 12) {
        left = bounds.left - panelWidth - 12;
      }
      left = Math.max(12, Math.min(left, window.innerWidth - panelWidth - 12));
      const top = Math.max(12, Math.min(bounds.top, window.innerHeight - 120));
      setPosition({ left, top });
    };

    updatePosition();
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updatePosition)
        : null;
    resizeObserver?.observe(anchor);
    if (anchor.parentElement) resizeObserver?.observe(anchor.parentElement);
    const mutationObserver =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(updatePosition)
        : null;
    for (
      let element = anchor;
      element && element !== document.body;
      element = element.parentElement
    ) {
      mutationObserver?.observe(element, {
        attributes: true,
        attributeFilter: ["class", "style"],
      });
    }
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorRef, isVisible]);

  if (!isVisible || !actions.length) return null;

  return createPortal(
    <section
      className={`route-actions-overlay ${isExpanded ? "" : "is-collapsed"}`}
      aria-label={ariaLabel}
      style={{ left: position?.left ?? -10000, top: position?.top ?? 12 }}
    >
      <div className="route-actions-overlay-heading">
        <h2 className="route-actions-overlay-title">{title}</h2>
        <button
          type="button"
          className="route-actions-overlay-toggle"
          aria-label={isExpanded ? `Collapse ${title}` : `Expand ${title}`}
          aria-expanded={isExpanded}
          title={isExpanded ? "Collapse" : "Expand"}
          disabled={isToggleDisabled}
          onClick={() => setIsExpanded((current) => !current)}
        >
          {isExpanded ? (
            <IconChevronUp size={17} />
          ) : (
            <IconChevronDown size={17} />
          )}
        </button>
      </div>
      <div
        className={`route-actions-overlay-content ${isExpanded ? "is-expanded" : ""}`}
        aria-hidden={!isExpanded}
      >
        <div className="route-actions-overlay-list">
          {actions.map((action) => (
            <div className="route-actions-overlay-item" key={action.id}>
              <button
                type="button"
                className="route-actions-overlay-button"
                onClick={() => action.onClick?.()}
                disabled={Boolean(action.disabled)}
                tabIndex={isExpanded ? 0 : -1}
                title={action.label}
              >
                <span className="route-actions-overlay-icon" aria-hidden="true">
                  {action.icon}
                </span>
                <span>{action.label}</span>
              </button>
              {action.onEdit ? (
                <button
                  type="button"
                  className="route-actions-overlay-edit-button"
                  aria-label={`Edit ${action.label}`}
                  title={`Edit ${action.label}`}
                  tabIndex={isExpanded ? 0 : -1}
                  disabled={Boolean(action.editDisabled)}
                  onClick={(event) => {
                    event.stopPropagation();
                    action.onEdit();
                  }}
                >
                  <IconPencil size={15} aria-hidden="true" />
                </button>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </section>,
    document.body,
  );
}

export default AiToolsPanel;
